import moment from 'moment';
import { action, makeObservable, observable, override, runInAction, computed } from 'mobx';
import {
	CHINA_NATIONALITY_VALUE,
	IMonthsOption,
	MONTH_OPTIONS,
	ONBOARDING_CURRENCY_TYPE,
	ONBOARDING_STATUSES,
	ONBOARDING_TYPE
} from 'src/constants';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { ResponseDTO } from 'src/dto/base.dto';
import {
	AccountDetailDTO,
	BankAccountDTO,
	CreateFunderAccountDetailResponse,
	EmploymentDetailOnboardingDTO,
	FunderOnboardingProgressDTO,
	ICreateFunderAccountRequest,
	IFunderFSA,
	IFunderOnboardingProgress,
	IndividualFunderOnboardingFsaDTO,
	IndividualSignFsaDTO,
	LoanLendingDTO,
	SaveDraftDTO,
	SubmitDTO
} from 'src/dto/onboarding.dto';
import {
	IAccountAddress,
	IAccountDetail,
	IBankAccount,
	IEmploymentDetail,
	IFunderSupportingDocument,
	ILoanLendingCriteria
} from 'src/interfaces/accounts/account';
import { IHttpService } from 'src/services/http.service';
import { IBaseOnboardingStore, IOnboardingStore, OnboardingStore } from './onboarding.store';
import { convertNumberToStringForObject, convertStringToNumberForObject } from 'src/helpers/common.utils';
import { IUserStore } from '.';
export const LOAN_LENDING_CONVERTING_COLUMNS: Array<keyof ILoanLendingCriteria> = [
	'maxLoanPerDeal',
	'totalCommittedLoanAmount'
];

interface IStepValidattion {
	loanLendingCriteria?: boolean;
	bankAccount?: boolean;
}
export interface IIndividualFunderOnboardingStore extends IBaseOnboardingStore {
	onboardingStatus: ONBOARDING_STATUSES;
	onboardingType: ONBOARDING_TYPE;
	onboardingCurrency: ONBOARDING_CURRENCY_TYPE;
	defaulOnboardingCurrency: ONBOARDING_CURRENCY_TYPE;
	onboardingFSA?: IFunderFSA;

	employmentDetail?: IEmploymentDetail;
	loanLendingCriteria?: ILoanLendingCriteria;
	defaultLoanLendingCriteria?: ILoanLendingCriteria;
	bankAccount?: IBankAccount;

	submittedToKyc?: boolean;

	stepValidattion: IStepValidattion;

	updateLoanLendingCriteria(newData: ILoanLendingCriteria): void;

	setOnboardingCurrency(currency: ONBOARDING_CURRENCY_TYPE): void;
	setSupportingDocument(documentList: IFunderSupportingDocument): void;
	saveEmploymentDetail(body: IEmploymentDetail): Promise<boolean>;
	saveLoanLendingCriteria(body: ILoanLendingCriteria): Promise<boolean>;
	saveBankAccount(body: IBankAccount): Promise<boolean>;
	getFunderFSA(): Promise<void>;
	signFSA(): Promise<boolean>;
	submitFsaFile(formData: FormData): Promise<boolean>;
	dispose(): void;
	updateMinimumLoanPeriod(value: number): void;

	get monthsMaximumOption(): IMonthsOption[];

	clearOtherInput(type: string): void;

	updateOccupation(value: string): void;

	updateEmploymentType(value: string): void;

	disableButtonSubmit(step: number): boolean;

	setStepValidation(step: IStepValidattion): void;

	get disabledSubmitBtn(): boolean;
	get isChineseNationality(): boolean;
	get isAllStepComplete(): boolean;


	downloadIndividualFunderRequestFile(): Promise<void>;

	downloadCorporateFunderRequestFile(): Promise<void>;
	updatePropertyCountry(value: string[]): void;
}

export class IndividualFunderOnboardingStore extends OnboardingStore implements IIndividualFunderOnboardingStore {
	onboardingStatus: ONBOARDING_STATUSES = ONBOARDING_STATUSES.InCompleted;
	onboardingType = ONBOARDING_TYPE.FUNDER;
	onboardingCurrency = ONBOARDING_CURRENCY_TYPE.HKD;
	defaulOnboardingCurrency = ONBOARDING_CURRENCY_TYPE.HKD;
	onboardingFSA?: IFunderFSA;
	employmentDetail?: IEmploymentDetail;
	loanLendingCriteria?: ILoanLendingCriteria;
	defaultLoanLendingCriteria?: ILoanLendingCriteria;
	bankAccount?: IBankAccount;

	supportingDocuments?: IFunderSupportingDocument;
	supportingDocumentsLocal?: IFunderSupportingDocument;

	submittedToKyc?: boolean = false;

	stepValidattion: IStepValidattion = {
		loanLendingCriteria: false,
		bankAccount: false
	}

	constructor(protected readonly http: IHttpService, private onboardingStore: IOnboardingStore, private userStore: IUserStore) {
		super(http);
		makeObservable(this, {
			employmentDetail: observable,
			loanLendingCriteria: observable.deep,
			defaultLoanLendingCriteria: observable.deep,
			onboardingStatus: observable,
			onboardingCurrency: observable,
			onboardingType: observable.deep,
			bankAccount: observable.deep,
			setOnboardingCurrency: action.bound,
			loadProgress: override,
			dispose: action,
			monthsMaximumOption: computed,
			updateMinimumLoanPeriod: action.bound,
			clearOtherInput: action.bound,
			updateOccupation: action.bound,
			updateEmploymentType: action.bound,
			disableButtonSubmit: action.bound,
			updatePropertyCountry: action.bound,
			disabledSubmitBtn: computed,
			isChineseNationality: computed,
			isAllStepComplete: computed,
			updateLoanLendingCriteria: action.bound,
			setStepValidation: action.bound,
			stepValidattion: observable.deep
		});
	}

	setOnboardingCurrency(currency: ONBOARDING_CURRENCY_TYPE): void {
		this.onboardingCurrency = currency;
	}

	setSupportingDocument(documentList: IFunderSupportingDocument): void {
		this.supportingDocuments = documentList;
	}

	get disabledSubmitBtn(): boolean {
		let hasEnoughDocument;
		if (this.accountDetail?.nationality === CHINA_NATIONALITY_VALUE) {
			hasEnoughDocument =
				this.supportingDocuments &&
				this.supportingDocuments.chineseIdImgFront &&
				this.supportingDocuments.chineseIdImgBack &&
				this.supportingDocuments.selfie &&
				this.supportingDocuments.residentialProof &&
				this.supportingDocuments.proofIncome &&
				this.supportingDocuments.bankCardImg
		} else {
			hasEnoughDocument =
				this.supportingDocuments &&
				this.supportingDocuments.identities &&
				this.supportingDocuments.selfie &&
				this.supportingDocuments.residentialProof &&
				this.supportingDocuments.proofIncome
		}

		// const isAllStepCompleted = Object.values(this.stepValidattion).every(item => Boolean(item));

		if (this.loanLendingCriteria?.id && this.bankAccount?.id && hasEnoughDocument) {
			return false;
		}

		return true;
	}

	public updateLoanLendingCriteria(newData: ILoanLendingCriteria): void {
		this.loanLendingCriteria = {
			...newData
		}
	}
	async saveEmploymentDetail(body: IEmploymentDetail): Promise<boolean> {
		const dto = new EmploymentDetailOnboardingDTO({
			...body,
			personId: this.accountDetail?.personId
		});

		const result: ResponseDTO<string> = await this.http.request<EmploymentDetailOnboardingDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.employmentDetail = {
					...body,
					id: result.data
				};
			});
			return true;
		}
		return false;
	}
	async saveLoanLendingCriteria(body: ILoanLendingCriteria): Promise<boolean> {
		body = convertStringToNumberForObject<ILoanLendingCriteria>(body, LOAN_LENDING_CONVERTING_COLUMNS);
		if (this.loanLendingCriteria?.id) body.id = this.loanLendingCriteria.id;
		const dto = new LoanLendingDTO({
			...body,
			personId: this.accountDetail?.personId
		});
		const result: ResponseDTO<string> = await this.http.request<LoanLendingDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.loanLendingCriteria = {
					...convertNumberToStringForObject<ILoanLendingCriteria>(body, LOAN_LENDING_CONVERTING_COLUMNS),
					id: result.data
				};

				this.defaultLoanLendingCriteria = { ...this.loanLendingCriteria };
				this.onboardingCurrency = this.defaulOnboardingCurrency = body.currency as ONBOARDING_CURRENCY_TYPE;
			});
			return true;
		}
		return false;
	}

	async saveBankAccount(body: IBankAccount): Promise<boolean> {
		const dto = new BankAccountDTO({
			...body,
			personId: this.accountDetail?.personId
		});
		const result: ResponseDTO<string> = await this.http.request<BankAccountDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.bankAccount = {
					...body,
					id: result.data
				};
			});
			return true;
		}
		return false;
	}

	async saveDraft(): Promise<boolean> {
		const dto = new SaveDraftDTO();
		const result: ResponseDTO<string> = await this.http.request<SaveDraftDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.Draft;
			});
			return true;
		}
		return false;
	}

	async submit(): Promise<boolean> {
		const dto = new SubmitDTO();
		const result: ResponseDTO<string> = await this.http.request<SubmitDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.WaitingForApproval;
			});
			return true;
		}
		return false;
	}

	async loadProgress(): Promise<boolean> {
		const dto = new FunderOnboardingProgressDTO();
		const result = await this.http.request<FunderOnboardingProgressDTO, IFunderOnboardingProgress>(dto);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.accountDetail = result.data?.accountDetail;
				this.employmentDetail = result.data?.employmentOnboardingDetail;
				if (this.loanLendingCriteria) {
					this.onboardingStatus = ONBOARDING_STATUSES.Draft;
				}
				if (result.data && result.data.loanLendingCriteriaOnboarding) {
					this.loanLendingCriteria = {
						...result.data.loanLendingCriteriaOnboarding,
						...convertNumberToStringForObject<ILoanLendingCriteria>(
							result.data.loanLendingCriteriaOnboarding,
							LOAN_LENDING_CONVERTING_COLUMNS
						)
					};

					this.defaultLoanLendingCriteria = { ...this.loanLendingCriteria };

					this.onboardingCurrency = this.defaulOnboardingCurrency = result.data.referenceCurrency ?? ONBOARDING_CURRENCY_TYPE.HKD;
				}
				this.bankAccount = result.data?.bankAccountOnboarding;
				this.supportingDocuments = result.data?.supportingDocuments;
				this.submittedToKyc = result.data?.submittedToKyc;
				if (this.accountDetail?.dob) {
					this.accountDetail.dob = moment(this.accountDetail.dob);
				}
				this.setFunderProgressLoaded();

				if(result.data?.accountDetail?.personId) {
					this.userStore.setPersonId(result.data?.accountDetail?.personId)
				}
			});
			return true;
		}
		return false;
	}

	public async saveAccountDetail(body: IAccountDetail): Promise<boolean> {
		const em = convertStringToNumberForObject<IEmploymentDetail>(body.employmentDetail || {}, ['annualSalary']);
		const bodyDto: ICreateFunderAccountRequest = {
			...body,
			personId: this.accountDetail?.personId,
			dateOfBirth: body.dob?.toDate(),
			personalAccountType: this.onboardingType,
			employmentDetail: em,
			residentialAddress: body.residentialAddress as IAccountAddress
		};

		if (body.mailingAddress) {
			bodyDto.mailingAddress = body.mailingAddress as IAccountAddress;
			bodyDto.mailingAddress.id = this.accountDetail?.mailingAddress?.id;

		}

		if (this.accountDetail?.residentialAddress?.id) {
			bodyDto.residentialAddress.id = this.accountDetail.residentialAddress.id;
		}

		const dto = new AccountDetailDTO(bodyDto);
		const result: ResponseDTO<CreateFunderAccountDetailResponse> = await this.http.request<
			AccountDetailDTO,
			CreateFunderAccountDetailResponse
		>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.accountDetail = {
					...this.accountDetail,
					...body,
					personId: result.data?.personId,
					employmentDetail: convertNumberToStringForObject<IEmploymentDetail>(em, ['annualSalary'])
				};
			});
			return true;
		}
		return false;
	}

	public dispose(): void {
		this.accountDetail = undefined;
		this.borrowerOnboardingRequest = undefined;
		this.funderOnboardingRequest = undefined;
		this.employmentDetail = undefined;
		this.loanLendingCriteria = undefined;
		this.defaultLoanLendingCriteria = undefined;
	}

	public async getFunderFSA(): Promise<void> {
		const FunderFSADto = new IndividualFunderOnboardingFsaDTO();
		const result: ResponseDTO<IFunderFSA> = await this.http.request<IndividualFunderOnboardingFsaDTO, IFunderFSA>(
			FunderFSADto
		);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingFSA = result.data as IFunderFSA;
			});
		}
	}

	public async signFSA(): Promise<boolean> {
		const SignFSADto = new IndividualSignFsaDTO();
		const result: ResponseDTO<string> = await this.http.request<IndividualSignFsaDTO, string>(SignFSADto);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStore.setFunderOnboardingStatus(ONBOARDING_STATUSES.SignedFSA);
				this.onboardingFSA = undefined;
			});

			return true;
		}
		return false;
	}

	public async submitFsaFile(formData: FormData): Promise<boolean> {
		const rs = await this.http.uploadFile<string>(ENDPOINT.INDIVIDUAL_UPLOAD_FSA_FILE, formData);
		if (rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return true;
		}
		return false;
	}
	get monthsMaximumOption(): IMonthsOption[] {
		const minimumValue = this.loanLendingCriteria?.minLoanPeriod;

		if (minimumValue) {
			const monthsOptionMax = MONTH_OPTIONS.filter((i) => i.value >= minimumValue);

			return monthsOptionMax;
		}
		return [];
	}
	public updateMinimumLoanPeriod(value: number): void {
		this.loanLendingCriteria = {
			...this.loanLendingCriteria,
			minLoanPeriod: value
		};
	}

	public clearOtherInput(type: string): void {
		if (type === 'occupationOther') {
			this.employmentDetail = {
				...this.employmentDetail,
				occupationOther: undefined
			};
		}

		if (type === 'employmentTypeOther') {
			this.employmentDetail = {
				...this.employmentDetail,
				employmentTypeOther: undefined
			};
		}
	}

	public updateOccupation(value: string): void {
		this.employmentDetail = {
			...this.employmentDetail,
			occupation: value
		};
	}

	public updateEmploymentType(value: string): void {
		this.employmentDetail = {
			...this.employmentDetail,
			employmentType: value
		};
	}
	public updatePropertyCountry(value: string[]): void {
		this.loanLendingCriteria = {
			...this.loanLendingCriteria,
			propertyCountry: value
		};
	}

	public setStepValidation(step: IStepValidattion): void {
		this.stepValidattion = step;
	}

	public disableButtonSubmit(step: number): boolean {
		console.log(step);
		let hasEnoughDocument;
		if (this.accountDetail?.nationality === CHINA_NATIONALITY_VALUE) {
			hasEnoughDocument =
				this.supportingDocuments &&
					this.supportingDocuments.identities &&
					this.supportingDocuments.selfie &&
					this.supportingDocuments.residentialProof &&
					this.supportingDocuments.proofIncome &&
					this.supportingDocuments.chineseIdImgBack &&
					this.supportingDocuments.chineseIdImgFront
					? true
					: false;
		} else {
			hasEnoughDocument =
				this.supportingDocuments &&
					// this.supportingDocuments.identities &&
					this.supportingDocuments.selfie &&
					this.supportingDocuments.residentialProof &&
					this.supportingDocuments.proofIncome
					? true
					: false;
		}

		if (this.loanLendingCriteria?.id && this.bankAccount?.id && hasEnoughDocument) {
			return false;
		}

		return true;
	}

	get isChineseNationality(): boolean {
		return this.accountDetail?.nationality === CHINA_NATIONALITY_VALUE;
	}

	get isAllStepComplete(): boolean {
		return Object.values(this.stepValidattion).every(item => Boolean(item));
	}

	public async downloadIndividualFunderRequestFile(): Promise<void> {
		await this.http.downloadFile(
			ENDPOINT.DOWNLOAD_INDIVIDUAL_FUNDER_REQUEST_FILE,
			{},
			{
				name: 'FR.pdf'
			}
		);
	}

	public async downloadCorporateFunderRequestFile(): Promise<void> {
		await this.http.downloadFile(
			ENDPOINT.DOWNLOAD_CORPORATE_FUNDER_REQUEST_FILE,
			{},
			{
				name: 'FR.pdf'
			}
		);
	}
}
